import { useCallback, useEffect, useRef, useState } from 'react';
const NO_SCROLL_STATE = {
    canScrollUp: false,
    canScrollDown: false,
};
/**
 * Custom hook to manage scroll state for containers
 * Handles both regular and reversed (auto-scroll) flex layouts
 */
export function useScrollState({ scrollContainerRef, enabled = true, autoScroll = false, children, }) {
    const [scrollState, setScrollState] = useState(NO_SCROLL_STATE);
    const animationFrameRef = useRef(null);
    // Calculate if scrolling is possible (binary state for snap behavior)
    const updateScrollState = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container)
            return;
        const absScrollTop = Math.abs(container.scrollTop);
        const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
        // Check if content is scrollable at all
        const isScrollable = maxScroll > 0;
        if (autoScroll) {
            // flex-col-reverse: scrollTop 0 = visual bottom, maxScroll = visual top
            // Can scroll up if there's content above current position
            // Can scroll down if there's content below current position
            const nextState = {
                canScrollUp: isScrollable && absScrollTop < maxScroll,
                canScrollDown: isScrollable && absScrollTop > 0,
            };
            setScrollState((prevState) => prevState.canScrollUp === nextState.canScrollUp &&
                prevState.canScrollDown === nextState.canScrollDown
                ? prevState
                : nextState);
            return;
        }
        // flex-col: scrollTop 0 = visual top, maxScroll = visual bottom
        // Can scroll up if we've scrolled down from top
        // Can scroll down if there's more content below
        const nextState = {
            canScrollUp: isScrollable && absScrollTop > 0,
            canScrollDown: isScrollable && absScrollTop < maxScroll,
        };
        setScrollState((prevState) => prevState.canScrollUp === nextState.canScrollUp &&
            prevState.canScrollDown === nextState.canScrollDown
            ? prevState
            : nextState);
    }, [autoScroll, scrollContainerRef]);
    const scheduleScrollStateUpdate = useCallback(() => {
        if (!enabled || animationFrameRef.current !== null)
            return;
        animationFrameRef.current = window.requestAnimationFrame(() => {
            animationFrameRef.current = null;
            updateScrollState();
        });
    }, [enabled, updateScrollState]);
    useEffect(() => () => {
        if (animationFrameRef.current === null)
            return;
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
    }, []);
    // Initial check for scroll position on component mount
    useEffect(() => {
        if (!enabled) {
            setScrollState((prevState) => prevState.canScrollUp || prevState.canScrollDown ? NO_SCROLL_STATE : prevState);
            return;
        }
        updateScrollState();
    }, [enabled, updateScrollState]);
    // Update on scroll events
    useEffect(() => {
        if (!enabled)
            return;
        const container = scrollContainerRef.current;
        if (!container)
            return;
        container.addEventListener('scroll', scheduleScrollStateUpdate, { passive: true });
        return () => container.removeEventListener('scroll', scheduleScrollStateUpdate);
    }, [enabled, scheduleScrollStateUpdate, scrollContainerRef]);
    // Update when content changes
    useEffect(() => {
        if (!enabled)
            return;
        scheduleScrollStateUpdate();
        const container = scrollContainerRef.current;
        if (!container)
            return;
        const observer = new MutationObserver(scheduleScrollStateUpdate);
        observer.observe(container, {
            childList: true,
        });
        const resizeObserver = typeof ResizeObserver === 'undefined'
            ? undefined
            : new ResizeObserver(scheduleScrollStateUpdate);
        resizeObserver?.observe(container);
        const contentElement = container.firstElementChild;
        if (contentElement instanceof Element) {
            resizeObserver?.observe(contentElement);
        }
        return () => {
            observer.disconnect();
            resizeObserver?.disconnect();
        };
    }, [children, enabled, scheduleScrollStateUpdate, scrollContainerRef]);
    return scrollState;
}
//# sourceMappingURL=useScrollState.js.map